/* ------------------------------------------------------------------
   Shared helpers for the public-facing sites (PersonalWebsite.js /
   BusinessWebsite.js). Pure functions only — no React, no side effects
   beyond the DOM patch in onImgErr, so both pages can import the same
   logic instead of re-defining it. Behaviour is unchanged from what
   each page did before this refactor.
------------------------------------------------------------------ */

/** Resolve a possibly-relative backend path into an absolute image URL. */
export function resolveImageUrl(apiBase, url) {
    if (!url) return "";
    return /^https?:\/\//i.test(url) ? url : `${apiBase}/${String(url).replace(/^\/+/, "")}`;
}

/** "Jane Doe" -> "JD" (max 2 letters), safe against empty/blank names. */
export function getInitials(name, fallback = "DN") {
    const source = (name || "").trim();
    if (!source) return fallback;
    return source
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

/** Inline SVG shown when a stored image URL 404s or points at a non-image page. */
export const IMG_FALLBACK =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='480' height='360'>
            <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
                <stop offset='0' stop-color='#123524'/><stop offset='1' stop-color='#1f6b45'/>
            </linearGradient></defs>
            <rect width='100%' height='100%' fill='url(#g)'/>
            <text x='50%' y='50%' font-family='sans-serif' font-size='15' fill='#ffffffaa' text-anchor='middle'>Image unavailable</text>
        </svg>`
    );

/** <img onError={onImgErr}> — swaps in IMG_FALLBACK exactly once, no retry loop. */
export function onImgErr(e) {
    e.currentTarget.onerror = null;
    e.currentTarget.src = IMG_FALLBACK;
}

/** True if a "YYYY" year string is plausible, used for "X years in business". */
export function yearsSince(yearString) {
    const s = String(yearString || "").trim();
    if (!/^\d{4}$/.test(s)) return null;
    return Math.max(1, new Date().getFullYear() - parseInt(s, 10));
}
